import React from "react";
import axios from "axios";
import dayjs from "dayjs";
import { useQuery } from "@tanstack/react-query";

import { Button, Icon } from "../../../components/ui";
import { moneyFormatter } from "../../../core/utils/formatters";
import { usePrefetchQuote } from "../../hooks/quotes";
import StatusIcon from "../Quotes/QuoteList/StatusIcon";
import "./quote-workspace.scss";

const APPROVED = ["approved", "quote_sent", "accepted_by_client"];
const PENDING = ["pending", "to_review"];

const belongsTo = (quote, requestId) =>
  requestId != null && String(quote.request_id ?? requestId) === String(requestId);

/**
 * The box in front of each quote says, by colour, what that quote can be for
 * THIS request (same rules as StatusIcon / QuoteItem's chooser):
 *   green   approved, sent or accepted on this request — it can be chosen
 *   amber   waiting for review
 *   grey    draft, expired, rejected
 *   dashed  another request's quote for the same watch — never chosen here
 * Ticked = the quote chosen for this deal.
 */
const boxOf = (quote, requestId, chosenId, liveCalculation = false) => {
  const status = String(quote.status || "").toLowerCase();
  if (!liveCalculation && !belongsTo(quote, requestId)) return "other";
  if (chosenId != null && String(chosenId) === String(quote.id)) return "chosen";
  if (quote.quote_expired) return "idle";
  if (APPROVED.includes(status)) return "ready";
  if (PENDING.includes(status)) return "pending";
  return "idle";
};

const BOX_LABEL = {
  chosen: "Chosen for this deal",
  ready: "Can be chosen",
  pending: "Awaiting review",
  idle: "Can't be chosen yet",
  other: "Another request's quote",
};

const fetchRoster = async ({ productId, requestId, type }) => {
  const { data } = await axios.post("/api/quotes/list", {
    filters: { product_id: productId, request_id: requestId, type, source: "all" },
    sort: { field: "created_at", direction: "desc" },
    paging: { offset: 0, limit: 50 },
  });
  return data?.items ?? [];
};

/**
 * Every quote for this watch, in one list that stays on screen above the open
 * quote — this request's quotes first, then "Other requests for this watch".
 * Tapping a row opens that quote below; ticking a green box chooses it for the
 * deal (only where the caller passes `onChoose` — the request flow).
 */
export default function QuoteRoster({
  item,
  type,
  refresh = 0,
  openId,
  onOpen,
  chosenId,
  onChoose,
  onNew,
  newDisabled = false,
  newTitle,
  // PX live calculation: any quote for the watch can be chosen, approved or not
  // (QuoteItem's `liveCalculation` rule) — so no "other request" group either.
  liveCalculation = false,
}) {
  const requestId = item?.id ?? item?.request_id;
  const productId = item?.product_id;
  const quoteType = type || item?.request_type || "order";
  const prefetchQuote = usePrefetchQuote();
  const { data: quotes = [], isLoading, isError, refetch } = useQuery({
    // Under ["quotes", requestId] so every quote save that refreshes that
    // request's quotes (useUpsertQuote) refreshes this list too.
    queryKey: ["quotes", requestId, "roster", productId, quoteType, refresh],
    queryFn: () => fetchRoster({ productId, requestId, type: quoteType }),
    enabled: !!productId,
  });

  if (!productId) return null;

  const ours = (q) => liveCalculation || belongsTo(q, requestId);
  const mine = quotes.filter(ours);
  const others = quotes.filter((q) => !ours(q));

  const row = (quote) => {
    const box = boxOf(quote, requestId, chosenId, liveCalculation);
    const isOpen = openId != null && String(openId) === String(quote.id);
    const canChoose =
      !!onChoose && (liveCalculation || box === "ready" || box === "chosen");
    const who = [quote.user?.first_name, quote.user?.last_name].filter(Boolean).join(" ");
    const request =
      box === "other"
        ? [quote.client_first_name, quote.client_last_name].filter(Boolean).join(" ") ||
          `Request #${quote.request_id}`
        : "This request";

    return (
      <li key={quote.id} className={`qws-roster__row is-${box}${isOpen ? " is-open" : ""}`}>
        {canChoose ? (
          <input
            type="checkbox"
            className={`qws-roster__box is-${box}`}
            checked={box === "chosen"}
            aria-label={box === "chosen" ? BOX_LABEL.chosen : `Choose this quote for the deal`}
            onChange={() => onChoose(quote)}
          />
        ) : (
          <span className={`qws-roster__box is-${box}`} role="img" aria-label={BOX_LABEL[box]} title={BOX_LABEL[box]}>
            {box === "chosen" && <Icon name="Check" size="sm" />}
          </span>
        )}
        <button
          type="button"
          className="qws-roster__open"
          aria-current={isOpen ? "true" : undefined}
          onPointerEnter={() => prefetchQuote(quote.id)}
          onFocus={() => prefetchQuote(quote.id)}
          onTouchStart={() => prefetchQuote(quote.id)}
          onClick={() => onOpen?.(quote.id, quote)}
        >
          <span className="qws-roster__status">
            <StatusIcon item={quote} requestId={requestId} />
          </span>
          <span className="qws-roster__price">
            {moneyFormatter((quote.price || 0) / 100, quote.currency || "gbp")}
          </span>
          <span className="qws-roster__who">{who || "—"}</span>
          <span className="qws-roster__date">{quote.date ? dayjs(quote.date).format("DD MMM") : "—"}</span>
          <span className="qws-roster__request">{isOpen ? "Open below" : request}</span>
          <span className="qws-roster__chevron" aria-hidden="true">
            <Icon name={isOpen ? "ChevronDown" : "ChevronRight"} size="sm" />
          </span>
        </button>
      </li>
    );
  };

  return (
    <section className="qws-roster" aria-label="Quotes">
      <header className="qws-roster__head">
        <h3 className="qws-roster__title">
          Quotes <span className="qws-roster__count">{quotes.length}</span>
        </h3>
        <span className="qws-roster__legend" aria-hidden="true">
          <span><span className="qws-roster__key is-ready" />Can be chosen</span>
          <span><span className="qws-roster__key is-pending" />Awaiting review</span>
          {!liveCalculation && <span><span className="qws-roster__key is-other" />Not this request</span>}
        </span>
        {onNew && (
          <Button
            variant="secondary"
            size="sm"
            className="qws-roster__new"
            disabled={newDisabled}
            title={newTitle}
            leadingIcon={<Icon name="Plus" size="sm" />}
            onClick={onNew}
          >
            New quote
          </Button>
        )}
      </header>

      {isError ? (
        <p className="qws-roster__empty">
          Couldn't load the quotes.{" "}
          <Button variant="ghost" size="sm" onClick={() => refetch()}>
            Try again
          </Button>
        </p>
      ) : isLoading ? (
        <p className="qws-roster__empty">Loading quotes…</p>
      ) : quotes.length === 0 ? (
        <p className="qws-roster__empty">No quotes yet.</p>
      ) : (
        // A fixed window — about five rows — that scrolls inside itself, so a
        // watch with dozens of quotes never pushes the open quote off the page.
        <div className="qws-roster__scroll" tabIndex={0} aria-label="Quote list">
          <div className="qws-roster__cols" aria-hidden="true">
            <span>Status</span>
            <span>Price</span>
            <span>Quoted by</span>
            <span>Date</span>
            <span>Request</span>
            <span />
          </div>
          <ul className="qws-roster__list">{mine.map(row)}</ul>
          {others.length > 0 && (
            <>
              <p className="qws-roster__group">Other requests for this watch</p>
              <ul className="qws-roster__list">{others.map(row)}</ul>
            </>
          )}
        </div>
      )}
    </section>
  );
}
